import React, { FC, useState } from 'react';

import { LabeledValidationInput, ValidationInput } from './inputs';
import { text } from './text';
import { assertNever } from './util';

enum Region {
    US = 'US',
    EU = 'EU',
    APAC = 'APAC',
}

const regionText: { [key in Region]: string } = {
    [Region.US]: text.regions.US,
    [Region.EU]: text.regions.EU,
    [Region.APAC]: text.regions.APAC,
};

export interface FormState {
    /** First Name */
    first: string;
    /** Last Name */
    last: string;
    /** Email address */
    email: string;
    /** Company name */
    company: string;
    /** Desired hostname (domain name) */
    host: string;
    /** Region */
    region: Region;
}

const DEFAULT: FormState = {
    first: '',
    last: '',
    email: '',
    company: '',
    host: '',
    region: Region.US,
};

export const defaultFormState = (): FormState => {
    const storage = window.localStorage;
    if (!storage) return DEFAULT;
    const stateStr = storage.getItem('formState');
    if (!stateStr) return DEFAULT;
    try {
        return JSON.parse(stateStr);
    } catch (e) {
        return DEFAULT;
    }
};

const saveFormState = (formState: FormState): void => {
    const storage = window.localStorage;
    if (!storage) return;
    storage.setItem('formState', JSON.stringify(formState));
};

export const Form: FC<{
    form?: FormState;
    onChange?(form: FormState): void;
    onSubmit?(): void;
    disabled?: boolean;
    hostError?: string;
}> = ({ form = DEFAULT, onChange, onSubmit, disabled, hostError }) => {
    saveFormState(form);
    const [submitted, setSubmitted] = useState<boolean>(false);

    const formChange = ({
        target,
    }: React.ChangeEvent<HTMLInputElement>): void => {
        if (!onChange) return;
        const name = target.name as keyof FormState;
        const value = target.value;
        switch (name) {
            case 'first':
                return onChange({ ...form, first: value });
            case 'last':
                return onChange({ ...form, last: value });
            case 'email':
                return onChange({ ...form, email: value });
            case 'company':
                return onChange({ ...form, company: value });
            case 'host':
                return onChange({ ...form, host: value });
            case 'region':
                const region = value as FormState['region'];
                switch (region) {
                    case Region.US:
                    case Region.EU:
                    case Region.APAC:
                        return onChange({ ...form, region });
                    default:
                        return assertNever(region);
                }
            default:
                return assertNever(name);
        }
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit?.();
            }}
            className={submitted ? 'c-form--validate' : undefined}
        >
            <div className="c-columns">
                <LabeledValidationInput
                    type="text"
                    name="first"
                    placeholder={text.first}
                    onChange={formChange}
                    value={form.first}
                    required
                    disabled={disabled}
                />
                <LabeledValidationInput
                    type="text"
                    name="last"
                    placeholder={text.last}
                    onChange={formChange}
                    value={form.last}
                    required
                    disabled={disabled}
                />
            </div>
            <LabeledValidationInput
                type="email"
                name="email"
                placeholder={text.email}
                onChange={formChange}
                value={form.email}
                required
                disabled={disabled}
            />
            <LabeledValidationInput
                type="text"
                name="company"
                placeholder={text.company}
                onChange={formChange}
                value={form.company}
                required
                disabled={disabled}
            />
            <label>
                <ValidationInput
                    type="text"
                    name="host"
                    placeholder={text.host}
                    onChange={formChange}
                    value={form.host}
                    minLength={4}
                    maxLength={63}
                    pattern="[a-z][a-z0-9\-]+[a-z0-9]"
                    hint={text.hostHint}
                    required
                    disabled={disabled}
                />
                {hostError ? (
                    <div className="c-host-error">{hostError}</div>
                ) : (
                    <div className="c-host-sample">
                        {`https://${
                            form.host === '' ? text.hostExample : form.host
                        }.${text.domain}/`}
                    </div>
                )}
            </label>
            <label>{text.regionHeader}</label>
            {Object.values(Region).map((regionCode) => (
                <label key={regionCode}>
                    <input
                        type="radio"
                        name="region"
                        value={regionCode}
                        checked={form.region === regionCode}
                        onChange={formChange}
                        disabled={disabled}
                    />
                    {regionText[regionCode]}
                </label>
            ))}
            <input
                type="submit"
                value={text.submit}
                onClick={() => setSubmitted(true)}
                disabled={disabled}
            />
        </form>
    );
};
